import { playNotificationSound } from '../utils/soundUtils';
import { API_URL } from '../constants/string-constants';
import { getAuth } from 'firebase/auth';
import { toast } from 'react-toastify';

class NotificationManager {
  constructor() {
    if (NotificationManager.instance) {
      return NotificationManager.instance;
    }

    // Core state
    this.socket = null;
    this.counts = {
      dm: 0,
      friendRequests: 0,
      groups: 0,
      modReports: 0,
      total: 0
    };

    // Active conversation context
    this.activeContext = {
      type: 'none', // 'none' | 'dm' | 'group'
      id: null,
      isVisible: false
    };

    // Subscribers for count updates
    this.subscribers = new Set();

    // Track individual conversation/group unread counts
    this.conversationUnreadCounts = new Map(); // conversationId -> count
    this.groupUnreadCounts = new Map(); // groupId -> count

    // Debug mode from env var
    this.debug = false;

    NotificationManager.instance = this;
  }

  log(...args) {
    if (this.debug) {
      console.log('[NotificationManager]', ...args);
    }
  }

  // Initialize with socket
  initialize(socket) {
    this.log('Initializing with socket');
    if (this.socket) {
      this.cleanup();
    }

    this.socket = socket;
    this.registerSocketListeners();
  }

  // Register all notification-related socket listeners in one place
  registerSocketListeners() {
    if (!this.socket) {
      this.log('No socket available, skipping listener registration');
      return;
    }

    this.log('Registering socket listeners');

    // DM notifications
    this.socket.on('dm_notification', this.handleDMNotification.bind(this));

    // Group notifications
    this.socket.on('group_notification', this.handleGroupNotification.bind(this));

    // Friend requests
    this.socket.on('friend_request_received', this.handleFriendRequest.bind(this));

    // Mention notifications
    this.socket.on('mention_notification', this.handleMentionNotification.bind(this));

    // Mod report notifications
    this.socket.on('mod_report_notification', this.handleModReportNotification.bind(this));
  }

  // DM notification handlers
  handleDMNotification(notification) {
    this.log('DM notification received:', notification);

    // Always play sound
    playNotificationSound(0.6);

    // Check if we should increment count
    const shouldIncrement = this.shouldIncrementForDM(notification.conversationId);
    this.log('Should increment DM?', shouldIncrement, 'Context:', this.activeContext);

    if (shouldIncrement) {
      this.incrementDMCount(notification.conversationId);
    }
  }


  // Group notification handlers
  handleGroupNotification(notification) {
    this.log('Group notification received:', notification);

    // Always play sound
    playNotificationSound(0.6);

    // Check if we should increment count
    const shouldIncrement = this.shouldIncrementForGroup(notification.groupId);
    this.log('Should increment group?', shouldIncrement, 'Context:', this.activeContext);

    if (shouldIncrement) {
      this.incrementGroupCount(notification.groupId);
    }
  }


  // Friend request handlers
  handleFriendRequest(data) {
    playNotificationSound(0.6);
    this.counts.friendRequests++;
    this.recalculateTotalCount();
    
    const nickname = data.request?.sender?.nickname || 'Someone';
    toast.success(`${nickname} sent you a friend request!`);
  }


  handleMentionNotification(notification) {
    this.log('Mention notification received:', notification);
    // For now, just play sound
    playNotificationSound(0.8); // Louder for mentions
  }

  handleModReportNotification(notification) {
    this.log('Mod report notification received:', notification);
    playNotificationSound(0.6);
    this.counts.modReports++;
    this.notifySubscribers();
  }

  // Context management
  setActiveConversation(type, id, isVisible) {
    this.log('Setting active context:', { type, id, isVisible });
    const previousContext = { ...this.activeContext };
    this.activeContext = { type, id, isVisible };

    // If conversation becomes visible, mark as read
    if (isVisible && id && !previousContext.isVisible) {
      this.log('Conversation became visible, marking as read');
      if (type === 'dm') {
        this.markDMAsRead(id);
      } else if (type === 'group') {
        this.markGroupAsRead(id);
      }
    }
  }

  // Check if we should increment for a specific conversation
  shouldIncrementForDM(conversationId) {
    // Always increment if no active context
    if (this.activeContext.type === 'none') {
      return true;
    }

    // Don't increment if this is the active visible DM
    return !(
      this.activeContext.type === 'dm' &&
      this.activeContext.id === conversationId &&
      this.activeContext.isVisible
    );
  }

  shouldIncrementForGroup(groupId) {
    // Always increment if no active context
    if (this.activeContext.type === 'none') {
      return true;
    }

    // Don't increment if this is the active visible group
    return !(
      this.activeContext.type === 'group' &&
      this.activeContext.id === groupId &&
      this.activeContext.isVisible
    );
  }

  // Count management
  incrementDMCount(conversationId) {
    const currentCount = this.conversationUnreadCounts.get(conversationId) || 0;
    this.conversationUnreadCounts.set(conversationId, currentCount + 1);
    this.log(`Incremented DM count for ${conversationId}: ${currentCount} -> ${currentCount + 1}`);
    this.recalculateDMCount();
  }

  incrementGroupCount(groupId) {
    const currentCount = this.groupUnreadCounts.get(groupId) || 0;
    this.groupUnreadCounts.set(groupId, currentCount + 1);
    this.log(`Incremented group count for ${groupId}: ${currentCount} -> ${currentCount + 1}`);
    this.recalculateGroupCount();
  }

  // Mark as read
  async markDMAsRead(conversationId) {
    this.log('Marking DM as read:', conversationId);
    this.conversationUnreadCounts.set(conversationId, 0);
    this.recalculateDMCount();

    // Call API to mark as read on backend
    try {
      const auth = getAuth();
      if (auth.currentUser) {
        const token = await auth.currentUser.getIdToken();
        await fetch(API_URL + `direct-messages/${conversationId}/read`, {
          method: 'PUT',
          headers: {
            Authorization: 'Bearer ' + token,
          },
        });
      }
    } catch (error) {
      console.error('Error marking DM as read:', error);
    }
  }

  async markGroupAsRead(groupId) {
    this.log('Marking group as read:', groupId);
    this.groupUnreadCounts.set(groupId, 0);
    this.recalculateGroupCount();

    // Call API to mark as read on backend
    try {
      const auth = getAuth();
      if (auth.currentUser) {
        const token = await auth.currentUser.getIdToken();
        await fetch(API_URL + `groups/${groupId}/read`, {
          method: 'PUT',
          headers: {
            Authorization: 'Bearer ' + token,
          },
        });
      }
    } catch (error) {
      console.error('Error marking group as read:', error);
    }
  }

  // Recalculate counts
  recalculateDMCount() {
    const dmTotal = Array.from(this.conversationUnreadCounts.values()).reduce((a, b) => a + b, 0);
    this.counts.dm = dmTotal;
    this.recalculateTotalCount();
  }

  recalculateGroupCount() {
    const groupTotal = Array.from(this.groupUnreadCounts.values()).reduce((a, b) => a + b, 0);
    this.counts.groups = groupTotal;
    this.recalculateTotalCount();
  }

  recalculateTotalCount() {
    this.counts.total = this.counts.dm + this.counts.groups + this.counts.friendRequests;
    this.log('Updated counts:', this.counts);
    this.notifySubscribers();
  }

  // Update friend request count
  setFriendRequestCount(count) {
    this.counts.friendRequests = count;
    this.recalculateTotalCount();
  }

  // Update mod report count
  setModReportCount(count) {
    this.counts.modReports = count;
    this.notifySubscribers();
  }

  // Set a preliminary total count (used before detailed breakdown is available)
  setPreliminaryTotalCount(totalCount) {
    this.log('Setting preliminary total count:', totalCount);
    // Store this in a special field that gets overridden when detailed counts load
    this.preliminaryTotal = totalCount;
    this.counts.total = totalCount;
    this.notifySubscribers();
  }

  // Override with detailed counts (clears preliminary count)
  overrideWithDetailedCounts() {
    this.log('Overriding preliminary count with detailed counts');
    this.preliminaryTotal = null;
    this.recalculateTotalCount(); // This will recalculate from individual conversation/group counts
  }

  // Bulk update counts (for initial load)
  setConversationCounts(conversationCounts) {
    this.log('Setting conversation counts:', conversationCounts);
    this.conversationUnreadCounts.clear();
    Object.entries(conversationCounts).forEach(([id, count]) => {
      if (count > 0) {
        this.conversationUnreadCounts.set(id, count);
      }
    });
    this.recalculateDMCount();
  }

  setGroupCounts(groupCounts) {
    this.log('Setting group counts:', groupCounts);
    this.groupUnreadCounts.clear();
    Object.entries(groupCounts).forEach(([id, count]) => {
      if (count > 0) {
        this.groupUnreadCounts.set(id, count);
      }
    });
    this.recalculateGroupCount();
  }

  // Subscription management
  subscribe(callback) {
    this.subscribers.add(callback);
    // Immediately call with current counts
    callback(this.counts);

    return () => {
      this.subscribers.delete(callback);
    };
  }

  notifySubscribers() {
    this.subscribers.forEach(callback => {
      try {
        callback(this.counts);
      } catch (error) {
        console.error('Error in notification subscriber:', error);
      }
    });
  }

  // Load initial counts from API
  async loadInitialCounts() {
    try {
      const auth = getAuth();
      if (!auth.currentUser) {
        return;
      }

      const token = await auth.currentUser.getIdToken();
      const response = await fetch(API_URL + 'notifications/unread-count', {
        headers: {
          Authorization: 'Bearer ' + token,
        },
      });

      if (response.ok) {
        const data = await response.json();
        // Set individual counts
        this.counts.dm = data.dm || 0;
        this.counts.groups = data.groups || 0;
        this.counts.friendRequests = data.friendRequests || 0;
        this.counts.total = data.total || 0;
        
        this.log('Loaded initial counts from API:', this.counts);
        this.notifySubscribers();
      }
    } catch (error) {
      console.error('Error loading initial counts:', error);
    }
  }

  // Cleanup
  cleanup() {
    this.log('Cleaning up socket listeners');
    if (this.socket) {
      this.socket.off('dm_notification');
      this.socket.off('group_notification');
      this.socket.off('friend_request_received');
      this.socket.off('mention_notification');
      this.socket.off('mod_report_notification');
    }
    this.socket = null;
  }

  // Reset everything
  reset() {
    this.log('Resetting notification manager');
    this.activeContext = { type: 'none', id: null, isVisible: false };
    this.conversationUnreadCounts.clear();
    this.groupUnreadCounts.clear();
    this.counts = { dm: 0, groups: 0, friendRequests: 0, modReports: 0, total: 0 };
    this.notifySubscribers();
  }

  // Get current counts
  getCounts() {
    return { ...this.counts };
  }

  // Get specific conversation unread count
  getConversationUnreadCount(conversationId) {
    return this.conversationUnreadCounts.get(conversationId) || 0;
  }

  // Get specific group unread count
  getGroupUnreadCount(groupId) {
    return this.groupUnreadCounts.get(groupId) || 0;
  }
}

// Create singleton instance
const notificationManager = new NotificationManager();

// Export for use in components
export default notificationManager;